/**
 * SliderRow: Gradient-styled range slider with label and formatted value.
 * Matches GradientSlider visual style (gradient track fill, white thumb).
 * Reads slider appearance settings from the store.
 */

import React, { useRef, useCallback } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { color, font, row } from './theme';

interface SliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
}

export default function SliderRow({ label, value, min, max, step, format, onChange }: SliderRowProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const settings = useEditorStore(s => s.interfaceSettings);

  const { sliderTrackHeight, sliderThumbSize } = settings;
  // Slider colour tracks the ACCENT theme tokens (dim → bright) exactly like the
  // Flow editor's FlowSlider, so it re-tints with each editor's accent instead
  // of the old fixed purple. The stored gradient colours are intentionally
  // ignored — the design system owns slider colour now.
  const sliderGradientFrom = 'var(--accent-dim)';
  const sliderGradientTo = 'var(--accent)';
  // Track fill + outline match the sidebar buttons (same surface tokens) so the
  // unfilled slider reads like a button with a visible outline.
  const sliderTrackBg = 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)';
  const sliderTrackBorderColor = 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)';

  // Convert pixel position to value in [min..max], snapped to step
  const posToValue = useCallback((clientX: number): number => {
    if (!trackRef.current) return value;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const raw = min + ratio * (max - min);
    // Snap to step
    const snapped = Math.round(raw / step) * step;
    // Clamp to [min, max] and round to avoid floating point drift
    return Math.max(min, Math.min(max, parseFloat(snapped.toFixed(10))));
  }, [min, max, step, value]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const newVal = posToValue(e.clientX);
    onChange(newVal);

    const onMove = (ev: MouseEvent) => {
      const v = posToValue(ev.clientX);
      onChange(v);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [posToValue, onChange]);

  // Percentage filled
  const range = max - min;
  const pct = range > 0 ? ((value - min) / range) * 100 : 0;

  // Label/value text is always white — the old typography colour tokens
  // (typoBodyColor/typoValueColor) defaulted to a purple-tinted grey, which is
  // the inconsistent text the design now unifies to white.
  const dynLabelStyle: React.CSSProperties = {
    ...labelStyle,
    fontSize: settings.typoBodySize - 1,
    color: color.ink,
  };
  const dynValueStyle: React.CSSProperties = {
    ...valueStyle,
    fontSize: settings.typoValueSize,
    color: color.ink,
  };

  return (
    <div style={rowStyle}>
      <span style={dynLabelStyle}>{label}</span>
      <div
        ref={trackRef}
        style={{
          ...trackStyle,
          height: sliderTrackHeight,
          background: sliderTrackBg,
          borderColor: sliderTrackBorderColor,
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Filled portion */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${sliderGradientFrom}, ${sliderGradientTo})`,
          borderRadius: 999,
          transition: 'width 0.05s ease',
        }} />
        {/* Thumb — accent ring (matches FlowSlider) */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: `${pct}%`,
          transform: 'translate(-50%, -50%)',
          width: sliderThumbSize,
          height: sliderThumbSize,
          borderRadius: '50%',
          background: 'var(--bg-1)',
          boxShadow: '0 0 8px var(--accent)',
          border: '2px solid var(--accent)',
          transition: 'left 0.05s ease',
        }} />
      </div>
      <span style={dynValueStyle}>
        {format(value)}
      </span>
    </div>
  );
}

const rowStyle: React.CSSProperties = row(12);

const labelStyle: React.CSSProperties = {
  // Wider, fixed column so long single words (Metalness, Anticipation…) never
  // overflow under the track. Matches Flow's settings label column.
  fontSize: 9, color: color.inkDim, width: 84, flexShrink: 0,
  fontFamily: font.sans,
};

const trackStyle: React.CSSProperties = {
  position: 'relative',
  flex: 1,
  borderRadius: 999,   // fully round (pill) ends
  cursor: 'pointer',
  border: '1px solid',
};

const valueStyle: React.CSSProperties = {
  fontSize: 9, color: color.inkFaint,
  fontFamily: font.sans,
  minWidth: 30, textAlign: 'right', whiteSpace: 'nowrap',
};
